/**
 * BrandGraphic — a small library of brand-consistent SVG graphics used
 * throughout the corporate site. Pure CSS / SMIL-free. All animations are
 * driven by tailwind animate-brand-* keyframes, so prefers-reduced-motion
 * users still see the static composition cleanly.
 *
 * Variants:
 *   - "mobility"  : road / motion line with traveling dot (purple/navy)
 *   - "records"   : stacked record bars rising on scroll (green)
 *   - "exchange"  : two arcs converging at center dot (blue)
 *   - "orbit"     : concentric rings with orbiting dot (navy)
 *   - "pulse"     : single radiating dot with ping ring (any tint)
 *   - "grid"      : faint topographic grid (decorative background)
 *   - "wave"      : sine-wave editorial divider
 *
 * Default brand palette:
 *   navy   #0F1D2D
 *   blue   #0E5FB0
 *   purple #5B2E83
 *   green  #2F8F3C
 */
const PALETTE = {
  navy: "#0F1D2D",
  blue: "#0E5FB0",
  purple: "#5B2E83",
  green: "#2F8F3C",
  sky: "#7AB8E8",
};

export default function BrandGraphic({
  variant = "mobility",
  color,
  className = "",
  ...rest
}) {
  const stroke = color || PALETTE.navy;

  if (variant === "mobility") {
    return (
      <svg
        viewBox="0 0 400 200"
        fill="none"
        className={className}
        aria-hidden="true"
        {...rest}
      >
        <defs>
          <linearGradient id="bg-mobility-fade" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor={stroke} stopOpacity="0" />
            <stop offset="40%" stopColor={stroke} stopOpacity="0.55" />
            <stop offset="100%" stopColor={stroke} stopOpacity="1" />
          </linearGradient>
        </defs>
        <path
          d="M10 160 C 80 160, 100 80, 200 80 S 320 160, 390 60"
          stroke="url(#bg-mobility-fade)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="1000"
          className="animate-brand-draw"
        />
        <path
          d="M10 160 C 80 160, 100 80, 200 80 S 320 160, 390 60"
          stroke={stroke}
          strokeWidth="1"
          strokeLinecap="round"
          strokeDasharray="3 8"
          opacity="0.25"
        />
        <circle r="5" fill={stroke}>
          <animateMotion
            dur="6s"
            repeatCount="indefinite"
            path="M10 160 C 80 160, 100 80, 200 80 S 320 160, 390 60"
          />
        </circle>
      </svg>
    );
  }

  if (variant === "records") {
    // 7 record bars, each animates fade-up with stagger via inline delay
    const bars = [40, 70, 55, 90, 65, 110, 80];
    return (
      <svg
        viewBox="0 0 280 140"
        fill="none"
        className={className}
        aria-hidden="true"
        {...rest}
      >
        {bars.map((h, i) => (
          <g key={i} style={{ animationDelay: `${i * 110}ms` }} className="animate-brand-fade-up">
            <rect
              x={20 + i * 36}
              y={130 - h}
              width="22"
              height={h}
              rx="2"
              fill={stroke}
              opacity={0.18 + i * 0.1}
            />
            <rect
              x={20 + i * 36}
              y={128 - h}
              width="22"
              height="2"
              rx="1"
              fill={stroke}
            />
          </g>
        ))}
        <line x1="10" y1="130" x2="270" y2="130" stroke={stroke} strokeWidth="1" opacity="0.5" />
      </svg>
    );
  }

  if (variant === "exchange") {
    return (
      <svg
        viewBox="0 0 320 200"
        fill="none"
        className={className}
        aria-hidden="true"
        {...rest}
      >
        <path
          d="M20 160 Q 160 20, 300 160"
          stroke={stroke}
          strokeWidth="1.5"
          strokeDasharray="600"
          className="animate-brand-draw"
        />
        <path
          d="M20 40 Q 160 180, 300 40"
          stroke={stroke}
          strokeWidth="1.5"
          strokeDasharray="600"
          opacity="0.5"
          className="animate-brand-draw"
          style={{ animationDelay: "300ms" }}
        />
        <circle cx="160" cy="100" r="6" fill={stroke} />
        <circle cx="160" cy="100" r="14" stroke={stroke} fill="none" className="animate-brand-ping-ring origin-center" style={{ transformOrigin: "160px 100px" }} />
        <text x="20" y="180" fill={stroke} fontSize="10" fontFamily="monospace" letterSpacing="2">JPY</text>
        <text x="270" y="180" fill={stroke} fontSize="10" fontFamily="monospace" letterSpacing="2">USD</text>
      </svg>
    );
  }

  if (variant === "orbit") {
    return (
      <svg
        viewBox="0 0 220 220"
        fill="none"
        className={className}
        aria-hidden="true"
        {...rest}
      >
        <circle cx="110" cy="110" r="100" stroke={stroke} strokeWidth="0.75" opacity="0.18" />
        <circle cx="110" cy="110" r="74" stroke={stroke} strokeWidth="0.75" opacity="0.28" />
        <circle cx="110" cy="110" r="50" stroke={stroke} strokeWidth="0.75" opacity="0.45" />
        <g className="animate-brand-orbit origin-center" style={{ transformOrigin: "110px 110px" }}>
          <circle cx="210" cy="110" r="5" fill={stroke} />
        </g>
        <g
          className="animate-brand-orbit origin-center"
          style={{ transformOrigin: "110px 110px", animationDuration: "16s", animationDirection: "reverse" }}
        >
          <circle cx="36" cy="110" r="3.5" fill={stroke} opacity="0.7" />
        </g>
        <g
          className="animate-brand-orbit origin-center"
          style={{ transformOrigin: "110px 110px", animationDuration: "9s" }}
        >
          <circle cx="160" cy="110" r="2.5" fill={stroke} opacity="0.85" />
        </g>
        <circle cx="110" cy="110" r="6" fill={stroke} />
      </svg>
    );
  }

  if (variant === "pulse") {
    return (
      <svg
        viewBox="0 0 80 80"
        fill="none"
        className={className}
        aria-hidden="true"
        {...rest}
      >
        <circle
          cx="40"
          cy="40"
          r="12"
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          className="animate-brand-ping-ring origin-center"
          style={{ transformOrigin: "40px 40px" }}
        />
        <circle
          cx="40"
          cy="40"
          r="20"
          fill="none"
          stroke={stroke}
          strokeWidth="1"
          opacity="0.3"
          className="animate-brand-ping-ring origin-center"
          style={{ transformOrigin: "40px 40px", animationDelay: "0.6s" }}
        />
        <circle cx="40" cy="40" r="6" fill={stroke} />
      </svg>
    );
  }

  if (variant === "wave") {
    return (
      <svg
        viewBox="0 0 600 80"
        fill="none"
        className={className}
        aria-hidden="true"
        preserveAspectRatio="none"
        {...rest}
      >
        <path
          d="M0 40 Q 75 0, 150 40 T 300 40 T 450 40 T 600 40"
          stroke={stroke}
          strokeWidth="1.5"
          strokeDasharray="1200"
          className="animate-brand-draw"
        />
        <path
          d="M0 50 Q 75 20, 150 50 T 300 50 T 450 50 T 600 50"
          stroke={stroke}
          strokeWidth="1"
          opacity="0.35"
          strokeDasharray="1200"
          className="animate-brand-draw"
          style={{ animationDelay: "300ms" }}
        />
      </svg>
    );
  }

  // "grid"
  return (
    <svg
      viewBox="0 0 600 400"
      fill="none"
      className={className}
      aria-hidden="true"
      preserveAspectRatio="xMidYMid slice"
      {...rest}
    >
      <defs>
        <pattern id="bg-brandgrid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke={stroke} strokeWidth="0.5" opacity="0.18" />
        </pattern>
        <radialGradient id="bg-brandgrid-fade" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="1" />
        </radialGradient>
      </defs>
      <rect width="600" height="400" fill="url(#bg-brandgrid)" />
      <rect width="600" height="400" fill="url(#bg-brandgrid-fade)" />
    </svg>
  );
}

export const BRAND_COLORS = PALETTE;
